import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import Paragraph from "antd/es/typography/Paragraph";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import { Deal, EntityType } from "@mapmycustomers/shared/types/entity";
import { Avatar, EntityTypeShapedIcon } from "@mapmycustomers/ui";

import ItemComponentProps from "@app/component/DnDBoard/types/ItemComponentProps";
import UserPreviewCard from "@app/component/UserPreviewCard";
import localSettings from "@app/config/LocalSettings";
import Path from "@app/enum/Path";
import DealInfo from "@app/scene/deal/component/DealInfo";
import ViewMode from "@app/scene/deal/enum/ViewMode";
import { showEntityView } from "@app/store/entityView/actions";
import colors from "@app/styles/_colors.module.scss";
import { formatDate } from "@app/util/formatters";
import isValidDate from "@app/util/isValidDate";
import { parseApiDateWithUtcTz } from "@app/util/parsers";

import styles from "./DealCard.module.scss";

interface Props extends ItemComponentProps<Deal> {
  showEntityView: typeof showEntityView;
}

const DealCard: React.FC<Props> = ({ item: deal, showEntityView }) => {
  const intl = useIntl();
  const history = useHistory();
  const { id, account, amount, closingDate, contact, name, stage, user } = deal;

  const handleDealClick = useCallback(() => {
    history.push(`${Path.DEAL}/${ViewMode.KANBAN}/${id}`);
  }, [history, id]);

  const handleCompanyClick = useCallback(() => {
    if (account) {
      showEntityView({ entityId: account.id, entityType: EntityType.COMPANY });
    }
  }, [account, showEntityView]);

  const handlePersonClick = useCallback(() => {
    if (contact) {
      showEntityView({ entityId: contact.id, entityType: EntityType.PERSON });
    }
  }, [contact, showEntityView]);

  const closingDateColor = useMemo(() => {
    if (stage.type === DealStageType.WON) {
      return "success";
    }
    if (stage.type === DealStageType.LOST) {
      return "error";
    }
    return "default";
  }, [stage]);

  const location = useMemo(() => {
    if (account?.geoAddress?.city) {
      return `${account.geoAddress.city}${
        account?.geoAddress.region ? `, ${account.geoAddress.region}` : ""
      }`;
    }
    if (contact?.geoAddress?.city) {
      return `${contact.geoAddress.city}${
        contact?.geoAddress.region ? `, ${contact.geoAddress.region}` : ""
      }`;
    }
    return intl.formatMessage({
      id: "dealCards.card.noLocation",
      defaultMessage: "No Location",
      description: "Deal card no location text",
    });
  }, [intl, account, contact]);

  const borderLeft = useMemo(() => {
    let color;
    if (deal.outOfCadence) {
      color = colors.red100;
    } else if (deal.rottingDaysOut && deal.stage.rottingInterval) {
      color = colors.yellow100;
    }
    if (color) {
      return `0.25rem solid ${color}`;
    }
    return "";
  }, [deal]);

  const parsedClosingDate = closingDate ? parseApiDateWithUtcTz(closingDate) : undefined;

  return (
    <div className={styles.container} onClick={handleDealClick} style={{ borderLeft }}>
      <div className={styles.title}>
        <Tooltip
          placement="top"
          title={intl.formatMessage({
            id: "dealCards.card.name.tooltip",
            defaultMessage: "Edit deal",
            description: "Deal card Name tooltip",
          })}
        >
          <Paragraph
            ellipsis={{
              expandable: false,
              rows: 2,
            }}
          >
            {name}
          </Paragraph>
        </Tooltip>
      </div>
      <div className={styles.subRow}>
        <div className={styles.amount}>
          {amount
            ? intl.formatNumber(amount, {
                currency: localSettings.getCurrencyCode(),
                minimumFractionDigits: 0,
                style: "currency",
              })
            : intl.formatMessage({
                id: "dealCards.card.noAmount",
                defaultMessage: "No Amount",
                description: "Deal card no amount text",
              })}
        </div>
        <div className={styles.location}>{location}</div>
      </div>
      <div className={styles.closingDate}>
        <Tag color={closingDateColor}>
          {isValidDate(parsedClosingDate) ? (
            <Tooltip
              placement="top"
              title={intl.formatMessage({
                id: "dealCards.card.closingDate.tooltip",
                defaultMessage: "Expected Close",
                description: "Deal card Expected Close tooltip",
              })}
            >
              {formatDate(parsedClosingDate, "P")}
            </Tooltip>
          ) : (
            intl.formatMessage({
              id: "dealCards.card.noCloseDate",
              defaultMessage: "No Closing Date",
              description: "Deal card no closing date text",
            })
          )}
        </Tag>
      </div>
      <div className={styles.bottom}>
        <div className={styles.icons}>
          <Tooltip title={user.fullName}>
            <UserPreviewCard userId={user.id}>
              <Avatar actionOnHover size="small" tooltip={false} user={user} />
            </UserPreviewCard>
          </Tooltip>
          <div className={styles.relations}>
            {account && (
              <Tooltip title={account.name}>
                <EntityTypeShapedIcon
                  className={styles.relationIconContainer}
                  entityType={EntityType.COMPANY}
                  iconClassName={styles.associationIcon}
                  onClick={handleCompanyClick}
                  size="xs"
                />
              </Tooltip>
            )}
            {contact && (
              <Tooltip title={contact.name}>
                <EntityTypeShapedIcon
                  className={styles.relationIconContainer}
                  entityType={EntityType.PERSON}
                  iconClassName={styles.associationIcon}
                  onClick={handlePersonClick}
                  size="xs"
                />
              </Tooltip>
            )}
          </div>
        </div>
        <DealInfo deal={deal} />
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  showEntityView,
};

export default connect(null, mapDispatchToProps)(DealCard);
